import type {
    BatchTextToVoiceoverRequest,
    BatchTextToVoiceoverResponse,
    TextToVoiceoverResponse,
} from 'js/src/client';
import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import { cacheVoiceoverDuration, processUrls } from 'js/src/data/ai-voices-and-avatars';
import { UNKNOWN_LANGUAGE_CODE } from 'js/src/libs/consts';
import WebSocketClient from 'js/src/libs/websocket-client';

export type ScriptSentenceData = Pick<
    TextToVoiceoverResponse,
    'script_sentence' | 'audible_duration'
>;
export type ScriptSentence = Pick<ScriptSentenceData, 'script_sentence'>;
type Response = { sentence_data: ScriptSentenceData } & Pick<TextToVoiceoverResponse, 'url'>;
type ResponseAndDate = {
    createdAt: Date;
    response: Response;
};

type ShortTermCacheEntry = Promise<void> | ResponseAndDate;

/**
 * While the voiceover is being generated, we store a promise in the short term cache.
 */
const shortTermCache = new Map<string, ShortTermCacheEntry>();

/**
 * Used to invalidate frontend cache immediately on code deploys.
 */
const suffixedCacheKey = '_voc';

export class VoiceoverCache {
    private getCacheKey(text: string, voice_preset: string, voiceSpeed: number = 1): string {
        // voiceSpeed is part of the key: the same text+voice at a different speed
        // produces different audio (and a different duration), so it must not
        // collide with the 1.0x render in the session cache.
        return (text.trim() + voice_preset + String(voiceSpeed) + suffixedCacheKey).toLowerCase();
    }

    private __getCache(key: string): ResponseAndDate | null {
        const value = sessionStorage.getItem(key);
        if (value) {
            return JSON.parse(value);
        }
        return null;
    }

    private __setCache(
        key: string,
        response: { url: string; sentence_data: ScriptSentenceData },
    ): void {
        const cacheData = {
            createdAt: new Date(),
            response,
        };
        try {
            sessionStorage.setItem(key, JSON.stringify(cacheData));
        } catch (_e) {
            // The cache is probably full, we should clear it and try again.
            sessionStorage.clear();
            sessionStorage.setItem(key, JSON.stringify(cacheData));
        }
        shortTermCache.set(key, cacheData);
    }

    public get(
        text: string,
        voice_preset: string,
        voiceSpeed: number = 1,
    ): { url: string | null; sentence_data: ScriptSentenceData } | null {
        // We don't need to re-fetch & re-generate the voiceover if we already have for the text + voice pair
        // Therefore we cache the response in the session storage so that subsequent calls use the cache
        const key = this.getCacheKey(text, voice_preset, voiceSpeed);
        const cache = this.__getCache(key);
        if (cache) {
            const cacheDate = new Date(cache['createdAt']);
            const response = cache['response'];
            if (cacheDate && response) {
                // Check if cache expiry period is reached
                cacheDate.setTime(cacheDate.getTime() + 3600000); // Cache expires after 1 hr
                if (new Date().getTime() < cacheDate.getTime()) {
                    const { url, sentence_data } = response;
                    return {
                        url,
                        sentence_data,
                    };
                }
            }
        }
        return null;
    }

    private async batchConvertTextToVoiceover({
        scriptSentences,
        voicePreset,
        regenerate,
        language_code = UNKNOWN_LANGUAGE_CODE,
        voiceSpeed = 1,
    }: {
        scriptSentences: Array<string>;
        voicePreset: string;
        regenerate: boolean;
        language_code?: string;
        voiceSpeed?: number;
    }): Promise<void> {
        const body: BatchTextToVoiceoverRequest = {
            blocks: scriptSentences
                .filter((sentence) => !this.get(sentence, voicePreset, voiceSpeed))
                .map((sentence) => ({
                    text: sentence,
                    voice_preset: voicePreset,
                    regenerate,
                    language_code,
                    voice_speed: voiceSpeed,
                })),
        };
        // It's possible we don't need to fetch anything if every sentence is already cached.
        if (body.blocks.length === 0) {
            return;
        }
        const triggerResponse = await ApiService.batchTextToVoiceoverCreate({
            body,
        });
        const { task_id } = triggerResponse.data;
        const response =
            await WebSocketClient.getInstance().waitForMessage<BatchTextToVoiceoverResponse>(
                task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'batch_text_to_voiceover',
                {
                    partialResponseCallback: processUrls(),
                },
            );
        // Cache the voiceover urls
        processUrls()(response);
    }

    public async fetchSentences({
        scriptSentences,
        voicePreset,
        regenerate = false,
        language_code = UNKNOWN_LANGUAGE_CODE,
        voiceSpeed = 1,
    }: {
        scriptSentences: Array<string>;
        voicePreset: string;
        regenerate?: boolean;
        language_code?: string;
        voiceSpeed?: number;
    }): Promise<void> {
        if (regenerate) {
            for (const sentence of scriptSentences) {
                this.delete(sentence, voicePreset, voiceSpeed);
            }
        }

        let sentencesNotInShortTermCache = scriptSentences.filter((sentence) => {
            const key = this.getCacheKey(sentence, voicePreset, voiceSpeed);
            return !shortTermCache.has(key);
        });

        if (sentencesNotInShortTermCache.length === 0) {
            // All promises resolved but the values are still not in the session cache?
            // Likely some of the sentences failed to generate and we need to re-fetch the full voiceover
            if (!scriptSentences.every((sentence) => this.get(sentence, voicePreset, voiceSpeed))) {
                sentencesNotInShortTermCache = scriptSentences;
            } else {
                await Promise.all(
                    scriptSentences.map((sentence) => {
                        const key = this.getCacheKey(sentence, voicePreset, voiceSpeed);
                        return shortTermCache.get(key);
                    }),
                );
                return;
            }
        }

        // Make requests for sentences not in short term cache
        const promise = this.batchConvertTextToVoiceover({
            scriptSentences: sentencesNotInShortTermCache,
            voicePreset,
            regenerate,
            language_code,
            voiceSpeed,
        });

        // Add promises to short term cache
        sentencesNotInShortTermCache.forEach((sentence) => {
            const key = this.getCacheKey(sentence, voicePreset, voiceSpeed);
            shortTermCache.set(key, promise);
        });

        await promise;
    }

    public set(
        text: string,
        voice_preset: string,
        response: { url: string; sentence_data: ScriptSentenceData },
        language_code: string = UNKNOWN_LANGUAGE_CODE,
        voiceSpeed: number = 1,
    ): void {
        const key = this.getCacheKey(text, voice_preset, voiceSpeed);
        if (!response.url) {
            throw new Error('Voiceover URL is empty');
        }
        this.__setCache(key, response);
        // text-to-duration caching is independent of voice
        const cleanedText = text.toLowerCase().trim();
        if (!sessionStorage.getItem(cleanedText)) {
            cacheVoiceoverDuration(cleanedText, response.url);
        }
    }

    public delete(text: string, voice_preset: string, voiceSpeed: number = 1): void {
        const key = this.getCacheKey(text, voice_preset, voiceSpeed);
        sessionStorage.removeItem(key);
        shortTermCache.delete(key);
    }

    async clear(): Promise<void> {
        sessionStorage.clear();

        await Promise.all(
            Array.from(shortTermCache.values()).map(async (value) => {
                if (value instanceof Promise) {
                    await value;
                }
            }),
        );

        shortTermCache.clear();
    }
}
